import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Warning, Fire, CheckCircle } from "@phosphor-icons/react";

const SEV_COLOR = {
  critical: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30",
  high: "bg-[#FFB000]/15 text-[#FFB000] border-[#FFB000]/30",
  medium: "bg-[#ffd166]/15 text-[#ffd166] border-[#ffd166]/30",
  low: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

export default function AIInsights() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open");

  const load = async () => {
    const { data } = await api.get(`/anomalies${filter ? `?status=${filter}` : ""}`);
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const resolve = async (id) => {
    await api.post(`/anomalies/${id}/resolve`);
    toast.success("Anomaly resolved");
    load();
  };

  return (
    <div className="space-y-6" data-testid="insights-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="data-label">Detection</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">AI Insights & Alerts</h1>
          <p className="text-slate-400 text-sm mt-2 max-w-2xl">Anomalies, SLA breaches, and root causes surfaced by the Anomaly Detection agent.</p>
        </div>
        <div className="flex gap-2">
          {["open", "resolved", ""].map((f) => (
            <Button key={f || "all"} size="sm"
                    data-testid={`filter-${f || "all"}`}
                    variant={filter === f ? "default" : "outline"}
                    onClick={() => setFilter(f)}
                    className={filter === f ? "bg-[#0055FF]" : "border-[#272A35] text-slate-300"}>
              {f === "" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-3">
        {items.length === 0 && <div className="text-sm text-slate-500 p-8 text-center itsme-card">No anomalies for this filter.</div>}
        {items.map((a) => (
          <div key={a.id} data-testid={`anomaly-${a.id}`}
               className={`itsme-card p-5 sev-${a.severity} itsme-card-hover`}>
            <div className="flex flex-col md:flex-row md:items-start md:gap-6">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  {a.severity === "critical" ? <Fire size={16} className="text-[#FF3B30]" /> :
                   <Warning size={16} className="text-[#FFB000]" />}
                  <div className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${SEV_COLOR[a.severity]}`}>
                    {a.severity}
                  </div>
                  {a.source === "servicenow" && (
                    <div className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border border-[#00E5FF]/30 text-[#00E5FF] bg-[#00E5FF]/5">
                      ServiceNow {a.external_ref ? `· ${a.external_ref}` : ""}
                    </div>
                  )}
                  <div className="text-xs text-slate-500">{new Date(a.detected_at).toLocaleString()}</div>
                </div>
                <div className="font-heading text-lg font-semibold">{a.title}</div>
                <div className="text-xs text-slate-400 mt-1">Process: <span className="text-slate-200">{a.process_name}</span></div>
                <div className="grid md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <div className="data-label">Root Cause</div>
                    <div className="text-sm text-slate-200 mt-1">{a.root_cause}</div>
                  </div>
                  <div>
                    <div className="data-label">Impact</div>
                    <div className="text-sm text-slate-200 mt-1">{a.impact}</div>
                  </div>
                </div>
              </div>
              <div className="flex md:flex-col gap-2 mt-4 md:mt-0">
                {a.status === "open" ? (
                  <Button size="sm" data-testid={`resolve-${a.id}`}
                          onClick={() => resolve(a.id)}
                          className="bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/30 hover:bg-[#00E676]/20">
                    <CheckCircle size={14} className="mr-1" /> Resolve
                  </Button>
                ) : (
                  <Badge className="bg-[#00E676]/15 text-[#00E676] border-[#00E676]/30">Resolved</Badge>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
